import React, { useState, useEffect, useRef, useCallback } from 'react';
import HeroParticles from './HeroParticles';
import RouletteButton from './RouletteButton';

const productPreviews = [
  { emoji: '📱', label: 'Phone Cases' },
  { emoji: '👕', label: 'T-Shirts' },
  { emoji: '🎒', label: 'Many others' },
];

// Words that cycle in the typewriter animation
const TYPEWRITER_WORDS = [
  'Wearable Art.',
  'Phone Cases.',
  'Unique Gifts.',
  'T-Shirt Prints.',
  'Wall Art.',
  'Your Vision.',
];

const TYPE_SPEED = 60;    // ms per character typed
const ERASE_SPEED = 35;   // ms per character erased
const PAUSE_AFTER_TYPE = 1800;  // ms to hold before erasing
const PAUSE_AFTER_ERASE = 300;  // ms between words

function useTypewriter(words) {
  const [display, setDisplay] = useState(words[0]);
  const [phase, setPhase] = useState('pause'); // 'typing' | 'pause' | 'erasing'
  const wordIndexRef = useRef(0);
  const charIndexRef = useRef(words[0].length);

  useEffect(() => {
    let timeout;

    if (phase === 'pause') {
      timeout = setTimeout(() => setPhase('erasing'), PAUSE_AFTER_TYPE);
    } else if (phase === 'erasing') {
      if (charIndexRef.current > 0) {
        timeout = setTimeout(() => {
          charIndexRef.current -= 1;
          setDisplay(words[wordIndexRef.current].slice(0, charIndexRef.current));
        }, ERASE_SPEED);
      } else {
        // Move to next word
        timeout = setTimeout(() => {
          wordIndexRef.current = (wordIndexRef.current + 1) % words.length;
          charIndexRef.current = 0;
          setDisplay('');
          setPhase('typing');
        }, PAUSE_AFTER_ERASE);
      }
    } else if (phase === 'typing') {
      const target = words[wordIndexRef.current];
      if (charIndexRef.current < target.length) {
        timeout = setTimeout(() => {
          charIndexRef.current += 1;
          setDisplay(target.slice(0, charIndexRef.current));
        }, TYPE_SPEED);
      } else {
        setPhase('pause');
      }
    }

    return () => clearTimeout(timeout);
  }, [phase, display, words]);

  return { display, phase };
}

// Hook: returns { x, y } in [-1, 1] range, lerped smoothly
function useParallaxMouse(sectionRef, { disabled = false } = {}) {
  const posRef = useRef({ x: 0, y: 0 });
  const targetRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (disabled) return;

    const onMouseMove = (e) => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // Normalize to [-1, 1]
      targetRef.current = {
        x: ((e.clientX - rect.left) / rect.width - 0.5) * 2,
        y: ((e.clientY - rect.top) / rect.height - 0.5) * 2,
      };
    };

    const onMouseLeave = () => {
      targetRef.current = { x: 0, y: 0 };
    };

    const lerp = (a, b, t) => a + (b - a) * t;
    const LERP_FACTOR = 0.06;

    const tick = () => {
      posRef.current.x = lerp(posRef.current.x, targetRef.current.x, LERP_FACTOR);
      posRef.current.y = lerp(posRef.current.y, targetRef.current.y, LERP_FACTOR);
      setOffset({ x: posRef.current.x, y: posRef.current.y });
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    const el = sectionRef.current;
    if (el) {
      el.addEventListener('mousemove', onMouseMove);
      el.addEventListener('mouseleave', onMouseLeave);
    }
    window.addEventListener('mouseleave', onMouseLeave);

    return () => {
      cancelAnimationFrame(rafRef.current);
      if (el) {
        el.removeEventListener('mousemove', onMouseMove);
        el.removeEventListener('mouseleave', onMouseLeave);
      }
      window.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [disabled, sectionRef]);

  return offset;
}

function Hero({ onRoulette }) {
  const [mounted, setMounted] = useState(false);
  const [flashColors, setFlashColors] = useState(null); // [color1, color2] for bg flash
  const { display, phase } = useTypewriter(TYPEWRITER_WORDS);
  const sectionRef = useRef(null);

  // Handle hero background color flash from roulette
  const handleFlashColors = useCallback((colors) => {
    setFlashColors(colors);
    setTimeout(() => setFlashColors(null), 1200);
  }, []);

  // Disable parallax for reduced-motion and mobile
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile =
    typeof window !== 'undefined' && window.innerWidth < 768;

  const parallax = useParallaxMouse(sectionRef, {
    disabled: prefersReducedMotion || isMobile,
  });

  // Layer multipliers
  const LAYER_HEADLINE = 8;   // px max shift for headline
  const LAYER_PARTICLES = 15; // px max shift for particles canvas
  const LAYER_ORBS = 10;      // px max shift for orbs

  const headlineStyle = {
    transform: `translate(${parallax.x * LAYER_HEADLINE}px, ${parallax.y * LAYER_HEADLINE * 0.5}px)`,
    willChange: 'transform',
  };
  const particlesStyle = {
    transform: `translate(${parallax.x * LAYER_PARTICLES}px, ${parallax.y * LAYER_PARTICLES * 0.5}px)`,
    willChange: 'transform',
  };
  // Wrapper styles move the orb group by parallax; inner CSS animation does the float
  const orbWrap1Style = {
    position: 'absolute',
    top: '-20%',
    left: '50%',
    transform: `translate(calc(-50% + ${parallax.x * LAYER_ORBS}px), ${parallax.y * LAYER_ORBS * 0.5}px)`,
    willChange: 'transform',
    pointerEvents: 'none',
    zIndex: 0,
  };
  const orbWrap2Style = {
    position: 'absolute',
    top: '10%',
    left: '15%',
    transform: `translate(${parallax.x * LAYER_ORBS * 0.7}px, ${parallax.y * LAYER_ORBS * 0.7}px)`,
    willChange: 'transform',
    pointerEvents: 'none',
    zIndex: 0,
  };
  const orbWrap3Style = {
    position: 'absolute',
    top: '5%',
    right: '10%',
    transform: `translate(${-parallax.x * LAYER_ORBS * 0.6}px, ${parallax.y * LAYER_ORBS * 0.6}px)`,
    willChange: 'transform',
    pointerEvents: 'none',
    zIndex: 0,
  };

  useEffect(() => {
    // Tiny delay so the browser has painted the initial frame before animating
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Computed background color flash overlay style
  const heroFlashStyle = flashColors
    ? {
        background: `linear-gradient(135deg, ${flashColors[0] || '#8b5cf6'}, ${flashColors[1] || '#ec4899'})`,
      }
    : {};

  return (
    <section
      ref={sectionRef}
      className={`hero${mounted ? ' hero--mounted' : ''}${flashColors ? ' hero--roulette-flash' : ''}`}
    >
      {/* Roulette color flash overlay */}
      {flashColors && (
        <div
          className="roulette-flash-overlay roulette-flash-overlay--active"
          style={heroFlashStyle}
          aria-hidden="true"
        />
      )}
      {/* Animated gradient orbs — outer wrapper handles parallax, inner handles CSS float animation */}
      <div style={orbWrap1Style}>
        <div className="hero-orb hero-orb-1 hero-orb--inner" />
      </div>
      <div style={orbWrap2Style}>
        <div className="hero-orb hero-orb-2 hero-orb--inner" />
      </div>
      <div style={orbWrap3Style}>
        <div className="hero-orb hero-orb-3 hero-orb--inner" />
      </div>
      {/* Floating particles — deepest parallax layer */}
      <HeroParticles parallaxStyle={particlesStyle} />

      <div className="hero-badge">✨ Powered by AI Image Generation</div>
      {/* Headline — shallowest parallax layer */}
      <h1 style={headlineStyle}>
        Turn Words Into<br />
        <span className="gradient-text typewriter-word">
          {display}
          <span className={`typewriter-cursor${phase === 'pause' ? ' typewriter-cursor--blink' : ''}`}>|</span>
        </span>
      </h1>
      <p>
        Type a prompt. Watch AI create a unique design. Order it printed on
        premium products — delivered to your door.
      </p>
      <div className="hero-products">
        {productPreviews.map((p) => (
          <div key={p.label} className="hero-product-badge">
            <span>{p.emoji}</span>
            <span>{p.label}</span>
          </div>
        ))}
      </div>
      <div className="gift-banner">
        <span className="gift-icon">🎁</span>
        <span>The perfect <strong>unique gift</strong> — no one else will ever own the same design.</span>
      </div>

      {/* Random Design Roulette */}
      {onRoulette && (
        <div className="hero-roulette-row" style={{ marginTop: '20px', position: 'relative', zIndex: 2 }}>
          <RouletteButton
            onSelect={(design, colors) => onRoulette(design, colors)}
            onFlashColors={handleFlashColors}
          />
          <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginTop: '6px' }}>
            Spin to discover a random community design
          </p>
        </div>
      )}
    </section>
  );
}

export default Hero;
